/**
 * AI Campaign & Smart Queue monitoring page.
 *
 * Read-side view for an `ai_assisted` campaign: funnel stats, the list of
 * AI contacts with their lifecycle status, and a per-contact event
 * timeline drawer. Also exposes the one write action operators need —
 * "Enrol contacts" (materialise the AI sidecars + start the outbound
 * sequence). See docs/AI_CAMPAIGNS.md.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDateTime } from "@/lib/format";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { AppLayout } from "@/components/AppLayout";
import { BackLink } from "@/components/BackLink";
import { PermissionGate } from "@/components/PermissionGate";
import {
  type AICampaignContact,
  type AIVoiceCall,
  enrollAICampaign,
  getAICampaignStats,
  getAIContactDetail,
  getAIContactTimeline,
  getCallRecordingUrl,
  listAIContacts,
} from "@/modules/ai_campaigns/api";
import { AgentSettingsPanel } from "@/modules/ai_campaigns/AgentSettingsPanel";
import { TestCallPanel } from "@/modules/ai_campaigns/TestCallPanel";

const STATUS_TONE: Record<string, string> = {
  device_ready: "bg-cvp-ok/20 text-cvp-ok",
  waiting_for_operator: "bg-cvp-ok/20 text-cvp-ok",
  operator_assigned: "bg-cvp-ok/20 text-cvp-ok",
  video_completed: "bg-cvp-ok/20 text-cvp-ok",
  appointment_confirmed: "bg-cvp-accent/20 text-cvp-accent",
  ai_call_in_progress: "bg-cvp-warn/20 text-cvp-warn",
  human_assistance_required: "bg-cvp-warn/20 text-cvp-warn",
  refused: "bg-cvp-danger/20 text-cvp-danger",
  cancelled: "bg-cvp-danger/20 text-cvp-danger",
  invalid_number: "bg-cvp-danger/20 text-cvp-danger",
  max_attempts_reached: "bg-cvp-danger/20 text-cvp-danger",
};

function statusTone(status: string): string {
  return STATUS_TONE[status] ?? "bg-nb-surface-2 text-nb-text-2";
}

/** Humanise a lifecycle status slug (e.g. ai_call_in_progress → "Ai call
 * in progress"). Avoids maintaining ~30 status labels × 15 locales for an
 * internal monitoring view; the slug set is stable + self-descriptive. */
function humanize(status: string): string {
  if (!status) return "—";
  return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ");
}

export default function AICampaignPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [openContact, setOpenContact] = useState<AICampaignContact | null>(null);

  const stats = useQuery({
    queryKey: ["ai-campaign-stats", id],
    queryFn: () => getAICampaignStats(id!),
    enabled: !!id,
    refetchInterval: 15000,
  });

  const contacts = useQuery({
    queryKey: ["ai-campaign-contacts", id],
    queryFn: () => listAIContacts(id!),
    enabled: !!id,
    refetchInterval: 15000,
  });

  const enroll = useMutation({
    mutationFn: () => enrollAICampaign(id!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ai-campaign-contacts", id] });
      void qc.invalidateQueries({ queryKey: ["ai-campaign-stats", id] });
    },
  });

  return (
    <AppLayout title={t("aiCampaign.title")}>
      <div className="mx-auto max-w-6xl px-4 py-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <BackLink fallback={`/campaigns/${id}`} className="text-sm text-nb-text-2 hover:underline">
              ← {t("aiCampaign.backToCampaign")}
            </BackLink>
          </div>
          <PermissionGate perm="campaign.launch">
            <button
              className="btn-primary"
              disabled={enroll.isPending}
              onClick={() => enroll.mutate()}
            >
              {enroll.isPending ? t("aiCampaign.enrolling") : t("aiCampaign.enrollContacts")}
            </button>
          </PermissionGate>
        </div>

        {enroll.isSuccess && (
          <p className="mb-3 text-sm text-cvp-ok">
            {t("aiCampaign.enrolledCount", { count: enroll.data.enrolled })}
          </p>
        )}

        {/* Stats strip — was silently blank while loading AND on a
            permanent fetch failure (stats.data stays undefined either
            way), giving no indication anything was even attempted. */}
        {stats.isLoading && (
          <p className="mb-6 text-sm text-nb-text-2">{t("common.loading")}</p>
        )}
        {stats.isError && (
          <p className="mb-6 text-sm text-cvp-danger">
            {t("aiCampaign.statsLoadError", "Could not load campaign stats.")}
          </p>
        )}
        {stats.data && (
          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label={t("aiCampaign.totalContacts")} value={stats.data.total_contacts} />
            <Stat label={t("aiCampaign.availableOperators")} value={stats.data.available_operators} />
            <Stat label={t("aiCampaign.outboundCapacity")} value={stats.data.outbound_capacity} />
            <Stat
              label={t("aiCampaign.inQueue")}
              value={
                (stats.data.by_status["device_ready"] ?? 0) +
                (stats.data.by_status["waiting_for_operator"] ?? 0)
              }
            />
          </div>
        )}

        {/* Agent settings — AI console only (this page is ai_assisted-scoped) */}
        {id && (
          <div className="mb-6 space-y-4">
            <AgentSettingsPanel campaignId={id} />
            <TestCallPanel campaignId={id} />
          </div>
        )}

        {/* Contacts table */}
        <div className="overflow-x-auto rounded-lg border border-nb-line">
          <table className="w-full text-sm">
            <thead className="bg-nb-surface-2 text-left text-nb-text-2">
              <tr>
                <th className="px-3 py-2">{t("aiCampaign.colName")}</th>
                <th className="px-3 py-2">{t("aiCampaign.colStatus")}</th>
                <th className="px-3 py-2">{t("aiCampaign.colLine")}</th>
                <th className="px-3 py-2">{t("aiCampaign.colAttempts")}</th>
                <th className="px-3 py-2">{t("aiCampaign.colNext")}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {(contacts.data ?? []).map((c) => (
                <tr key={c.id} className="border-t border-nb-line">
                  <td className="px-3 py-2 text-nb-text">
                    {c.first_name} {c.last_name}
                    <span className="ml-2 text-xs text-nb-text-2">{c.phone}</span>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`rounded px-2 py-0.5 text-xs ${statusTone(c.status)}`}>
                      {humanize(c.status)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-nb-text-2">{c.phone_line_type}</td>
                  <td className="px-3 py-2 text-nb-text-2">{c.attempt_count}</td>
                  <td className="px-3 py-2 text-nb-text-2">
                    {c.next_action_at ? formatDateTime(c.next_action_at) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      className="text-xs text-cvp-accent hover:underline"
                      onClick={() => setOpenContact(c)}
                    >
                      {t("aiCampaign.viewTimeline")}
                    </button>
                  </td>
                </tr>
              ))}
              {contacts.isLoading && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-nb-text-2">
                    {t("common.loading")}
                  </td>
                </tr>
              )}
              {contacts.isError && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-cvp-danger">
                    {t("aiCampaign.contactsLoadError", "Could not load contacts.")}
                  </td>
                </tr>
              )}
              {contacts.data && contacts.data.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-nb-text-2">
                    {t("aiCampaign.empty")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {openContact && (
        <TimelineDrawer contact={openContact} onClose={() => setOpenContact(null)} />
      )}
    </AppLayout>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-nb-line bg-nb-surface-2 p-3">
      <div className="text-2xl font-semibold text-nb-text">{value}</div>
      <div className="text-xs text-nb-text-2">{label}</div>
    </div>
  );
}

function transcriptToText(transcript: unknown): string {
  if (!transcript) return "";
  if (typeof transcript === "string") return transcript;
  if (Array.isArray(transcript)) {
    return transcript
      .map((turn) => {
        const t = turn as { role?: string; message?: string; text?: string };
        const who = t.role ? `${t.role}: ` : "";
        return `${who}${t.message ?? t.text ?? ""}`;
      })
      .filter(Boolean)
      .join("\n");
  }
  return JSON.stringify(transcript, null, 2);
}

function CallCard({ call }: { call: AIVoiceCall }) {
  const { t } = useTranslation();
  const [showTranscript, setShowTranscript] = useState(false);
  const recording = useQuery({
    queryKey: ["ai-call-recording", call.id],
    queryFn: () => getCallRecordingUrl(call.id),
    enabled: call.has_recording,
  });
  const transcriptText = transcriptToText(call.transcript);

  return (
    <div className="rounded-lg border border-nb-line bg-nb-surface-2 p-3">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium text-nb-text">
          {t("aiCampaign.callAttempt", { n: call.attempt_number })} ·{" "}
          {humanize(call.status)}
        </span>
        <span className="text-xs text-nb-text-2">
          {call.duration_seconds != null ? `${call.duration_seconds}s` : ""}
        </span>
      </div>
      {call.has_recording ? (
        recording.data ? (
          <audio controls className="w-full" src={recording.data} />
        ) : (
          <div className="text-xs text-nb-text-2">{t("aiCampaign.loadingRecording")}</div>
        )
      ) : (
        <div className="text-xs text-nb-text-2">{t("aiCampaign.noRecording")}</div>
      )}
      {transcriptText && (
        <div className="mt-2">
          <button
            className="text-xs text-cvp-accent hover:underline"
            onClick={() => setShowTranscript((s) => !s)}
          >
            {showTranscript ? t("aiCampaign.hideTranscript") : t("aiCampaign.showTranscript")}
          </button>
          {showTranscript && (
            <pre className="mt-1 max-h-48 overflow-y-auto whitespace-pre-wrap rounded bg-nb-surface p-2 text-xs text-nb-text-2">
              {transcriptText}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

function TimelineDrawer({
  contact,
  onClose,
}: {
  contact: AICampaignContact;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const timeline = useQuery({
    queryKey: ["ai-contact-timeline", contact.id],
    queryFn: () => getAIContactTimeline(contact.id),
  });
  const detail = useQuery({
    queryKey: ["ai-contact-detail", contact.id],
    queryFn: () => getAIContactDetail(contact.id),
  });

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/40" onClick={onClose}>
      <div
        className="h-full w-full max-w-md overflow-y-auto bg-nb-surface p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-nb-text">
            {contact.first_name} {contact.last_name}
          </h2>
          <button className="text-nb-text-2 hover:text-nb-text" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Voice calls — recordings + transcripts, under this beneficiary */}
        {(detail.data?.voice_calls ?? []).length > 0 && (
          <div className="mb-5">
            <h3 className="mb-2 text-sm font-semibold text-nb-text">
              {t("aiCampaign.calls")}
            </h3>
            <div className="space-y-3">
              {(detail.data?.voice_calls ?? []).map((c) => (
                <CallCard key={c.id} call={c} />
              ))}
            </div>
          </div>
        )}

        <h3 className="mb-2 text-sm font-semibold text-nb-text">
          {t("aiCampaign.timeline")}
        </h3>
        <ol className="space-y-3">
          {(timeline.data ?? []).map((e) => (
            <li key={e.id} className="border-l-2 border-nb-line pl-3">
              <div className="text-sm text-nb-text">
                {humanize(e.previous_status)} →{" "}
                <strong>{humanize(e.new_status)}</strong>
              </div>
              <div className="text-xs text-nb-text-2">
                {formatDateTime(e.occurred_at)} · {e.source}
                {e.reason ? ` · ${e.reason}` : ""}
              </div>
            </li>
          ))}
          {timeline.data && timeline.data.length === 0 && (
            <li className="text-sm text-nb-text-2">{t("aiCampaign.noEvents")}</li>
          )}
        </ol>
      </div>
    </div>
  );
}
